// Detailed Lead / Call Report — one row per call, joined with the customer's
// full CRM fields, callback schedule, lead-list and transfer/hangup metadata.
// Handles multiple campaigns (different CRM screens) via union columns.
import { useEffect, useMemo, useState } from "react";
import {
  FileText, Loader2, Download, FileSpreadsheet, Search, Info, ChevronLeft, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { MultiSelect, isoToday } from "./_daterange";

// CRM columns are appended after this many fixed core columns; used to visually
// separate the "call" columns from the "customer CRM" columns in the table.
export default function CallDetailReportPage() {
  const [opts, setOpts] = useState({
    campaigns: [], queues: [], groups: [], agents: [], dispositions: [],
    directions: [], dial_modes: [],
  });
  const [from, setFrom] = useState(isoToday());
  const [to, setTo] = useState(isoToday());
  const [campaignIds, setCampaignIds] = useState([]);
  const [queueIds, setQueueIds] = useState([]);
  const [groupIds, setGroupIds] = useState([]);
  const [agentIds, setAgentIds] = useState([]);
  const [dispositionIds, setDispositionIds] = useState([]);
  const [direction, setDirection] = useState("all");
  const [dialMode, setDialMode] = useState("all");
  const [answered, setAnswered] = useState("all");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dl, setDl] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    api.get("/reports/call-detail/filters")
      .then((r) => setOpts(r.data))
      .catch((e) => toast.error(formatErr(e)));
  }, []);

  const body = useMemo(() => {
    const b = { date_from: from, date_to: to };
    if (campaignIds.length) b.campaign_ids = campaignIds;
    if (queueIds.length) b.queue_ids = queueIds;
    if (groupIds.length) b.group_ids = groupIds;
    if (agentIds.length) b.agent_ids = agentIds;
    if (dispositionIds.length) b.disposition_ids = dispositionIds;
    if (direction !== "all") b.direction = direction;
    if (dialMode !== "all") b.dial_mode = dialMode;
    if (answered !== "all") b.answered = answered === "yes";
    return b;
  }, [from, to, campaignIds, queueIds, groupIds, agentIds, dispositionIds, direction, dialMode, answered]);

  async function run(opts = {}) {
    const p = opts.page ?? page;
    const ps = opts.pageSize ?? pageSize;
    setBusy(true);
    try {
      const r = await api.post("/reports/call-detail/run", { ...body, page: p, page_size: ps });
      setData(r.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  }
  useEffect(() => { run({ page: 1 }); /* eslint-disable-next-line */ }, []);

  async function exportFile(fmt) {
    setDl(fmt);
    try {
      const r = await api.post(`/reports/call-detail/export?fmt=${fmt}`, body, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lead-call-detail-${from}_to_${to}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setDl(""); }
  }

  const columns = data?.columns || [];
  const rows = data?.rows || [];
  const crmStart = columns.findIndex((c) => c.id.startsWith("crm__"));

  return (
    <div className="px-4 sm:px-6 py-6" data-testid="call-detail-report-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
            <FileText className="w-6 h-6 text-primary" /> Detailed Lead / Call Report
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            One row per call with full customer CRM fields, disposition, callback, timings and recording. Filter, then export.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => exportFile("csv")} disabled={!rows.length || dl} className="gap-1.5" data-testid="cd-export-csv">
            {dl === "csv" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} CSV
          </Button>
          <Button onClick={() => exportFile("xlsx")} disabled={!rows.length || dl} className="gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="cd-export-xlsx">
            {dl === "xlsx" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-3 mb-4" data-testid="cd-filters">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground block mb-1">From</label>
            <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)}
              className="h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="cd-date-from" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground block mb-1">To</label>
            <input type="date" value={to} min={from} max={isoToday()} onChange={(e) => setTo(e.target.value)}
              className="h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="cd-date-to" />
          </div>
          <MultiSelect label="Campaign" options={opts.campaigns} selected={campaignIds} onChange={setCampaignIds} testid="cd-filter-campaign" />
          <MultiSelect label="Inbound Queue" options={opts.queues} selected={queueIds} onChange={setQueueIds} testid="cd-filter-queue" />
          <MultiSelect label="User Group" options={opts.groups} selected={groupIds} onChange={setGroupIds} testid="cd-filter-group" />
          <MultiSelect label="Agent" options={opts.agents} selected={agentIds} onChange={setAgentIds} testid="cd-filter-agent" />
          <MultiSelect label="Disposition" options={opts.dispositions} selected={dispositionIds} onChange={setDispositionIds} testid="cd-filter-disposition" />
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground block mb-1">Direction</label>
            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger className="h-9 w-32" data-testid="cd-filter-direction"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="inbound">Inbound</SelectItem>
                <SelectItem value="outbound">Outbound</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground block mb-1">Dial Mode</label>
            <Select value={dialMode} onValueChange={setDialMode}>
              <SelectTrigger className="h-9 w-36" data-testid="cd-filter-dialmode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All modes</SelectItem>
                {opts.dial_modes.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground block mb-1">Answered</label>
            <Select value={answered} onValueChange={setAnswered}>
              <SelectTrigger className="h-9 w-28" data-testid="cd-filter-answered"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="yes">Answered</SelectItem>
                <SelectItem value="no">Not answered</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => { setPage(1); run({ page: 1 }); }} disabled={busy} className="gap-1.5 h-9" data-testid="cd-apply">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Apply
          </Button>
        </div>
      </Card>

      {data && (() => {
        const total = data.total_rows || 0;
        const ps = data.page_size || pageSize;
        const cur = data.page || page;
        const totalPages = Math.max(1, Math.ceil(total / ps));
        const startIdx = total === 0 ? 0 : (cur - 1) * ps + 1;
        const endIdx = Math.min(total, cur * ps);
        const goto = (np) => { setPage(np); run({ page: np }); };
        const changeSize = (v) => { const ns = parseInt(v, 10); setPageSize(ns); setPage(1); run({ page: 1, pageSize: ns }); };
        return (
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2" data-testid="cd-summary">
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <Badge variant="outline" className="text-xs" data-testid="cd-total">{total} calls</Badge>
              <span data-testid="cd-range">Showing {startIdx}–{endIdx}</span>
              {campaignIds.length !== 1 && crmStart >= 0 && (
                <span className="flex items-center gap-1">
                  <Info className="w-3.5 h-3.5" />
                  CRM columns are the combined fields of all matching campaigns.
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Rows</span>
              <Select value={String(ps)} onValueChange={changeSize}>
                <SelectTrigger className="h-8 w-[72px]" data-testid="cd-page-size"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="h-8 px-2" disabled={busy || cur <= 1}
                onClick={() => goto(cur - 1)} data-testid="cd-prev"><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-xs tabular-nums min-w-[72px] text-center" data-testid="cd-page-label">Page {cur} / {totalPages}</span>
              <Button variant="outline" size="sm" className="h-8 px-2" disabled={busy || cur >= totalPages}
                onClick={() => goto(cur + 1)} data-testid="cd-next"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        );
      })()}

      {/* Table */}
      <Card className="overflow-hidden" data-testid="cd-table-card">
        <div className="overflow-x-auto max-h-[65vh]">
          <table className="w-full text-xs whitespace-nowrap">
            <thead className="bg-muted/60 sticky top-0 z-10">
              <tr>
                {columns.map((c, i) => (
                  <th key={c.id}
                    className={`px-3 py-2 font-semibold text-[10px] uppercase tracking-wider text-left border-b border-border
                      ${i === crmStart ? "border-l-2 border-l-primary/40" : ""}
                      ${i >= crmStart && crmStart >= 0 ? "bg-sky-50/60 dark:bg-sky-950/20" : ""}`}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {busy && (
                <tr><td colSpan={columns.length || 1} className="px-3 py-10 text-center text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin inline" /> Loading…
                </td></tr>
              )}
              {!busy && rows.length === 0 && (
                <tr><td colSpan={columns.length || 1} className="px-3 py-10 text-center text-muted-foreground italic">
                  No calls for these filters.
                </td></tr>
              )}
              {!busy && rows.map((r, ri) => (
                <tr key={ri} className="border-t border-border/50 hover:bg-muted/20" data-testid={`cd-row-${ri}`}>
                  {columns.map((c, i) => (
                    <td key={c.id}
                      className={`px-3 py-1.5 ${i >= crmStart && crmStart >= 0 ? "bg-sky-50/30 dark:bg-sky-950/10" : ""}`}>
                      {String(r[c.id] ?? "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
